/**
 * DataScienceCanvas.js
 * Top-level orchestrator for the Digital A4 Canvas system.
 *
 * Features:
 *   - Dynamic Capacity: Default target 8 cards/page (2 columns × 4 rows).
 *     Auto-fallback: If 8 cards exceed safe printable A4 height, page re-paginates to 6 cards.
 *   - Auto-pagination & LocalStorage persistence.
 *   - Lightbox page index state: controls pop-out zoomed A4 view.
 */
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import A4Sheet from './A4Sheet';
import SheetLightbox from './SheetLightbox';
import AICaptureModal from './AICaptureModal';
import PDFExportModal from './PDFExportModal';
import styles from './DataScienceCanvas.module.css';
import { verifyOperationPassword } from '../../../../services/settingsService';

/**
 * Height / Content Overflow Check:
 * Evaluates whether 8 candidate cards will exceed the safe printable height of an A4 sheet.
 * Returns true if content overflows, signaling auto-fallback to 6 cards.
 */
function cardOverflows8(cards) {
  if (cards.length <= 6) return false;

  let col1Height = 0;
  let col2Height = 0;

  for (let i = 0; i < cards.length && i < 8; i++) {
    const c = cards[i];
    const topicLines = Math.ceil((c.topic || '').length / 28);
    const summaryLines = Math.ceil((c.summary || '').length / 32);

    let detailLines = 0;
    if (c.detail) {
      const codeLines = c.detail.split('\n').length;
      const charsLines = Math.ceil(c.detail.length / 36);
      detailLines = Math.max(codeLines, charsLines);
    }

    // Estimated visual height per card in pixels
    const cardHeight = 32 + (topicLines * 16) + (summaryLines * 14) + (detailLines > 0 ? (detailLines * 13 + 12) : 0);

    if (i % 2 === 0) {
      col1Height += cardHeight + 6;
    } else {
      col2Height += cardHeight + 6;
    }
  }

  // Safe max printable height per column for 4 cards is ~720px
  const MAX_SAFE_COL_HEIGHT = 720;
  return col1Height > MAX_SAFE_COL_HEIGHT || col2Height > MAX_SAFE_COL_HEIGHT;
}

/**
 * Distribute a flat array of notes into pages dynamically.
 * Target: 8 cards per page.
 * Fallback: 6 cards per page if overflow detected.
 */
function distributeToPages(notes) {
  const pages = [];
  let remaining = [...notes];

  while (remaining.length > 0) {
    if (remaining.length <= 6) {
      pages.push(remaining);
      break;
    }

    const candidate8 = remaining.slice(0, 8);
    const overflows = cardOverflows8(candidate8);

    if (overflows) {
      // Fallback to 6 cards for this page
      pages.push(remaining.slice(0, 6));
      remaining = remaining.slice(6);
    } else {
      // 8 cards fit cleanly!
      pages.push(candidate8);
      remaining = remaining.slice(candidate8.length);
    }
  }

  if (pages.length === 0) pages.push([]);
  return pages;
}


function OperationPasswordModal({ onClose, onUnlocked }) {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async (event) => {
    event.preventDefault();
    if (!/^\d{8}$/.test(password)) return setError('Enter all 8 digits.');
    setLoading(true); setError('');
    try {
      const valid = await verifyOperationPassword(password);
      if (!valid) return setError('Incorrect operation password.');
      onUnlocked();
    } catch (requestError) {
      setError(requestError.message || 'Unable to verify the operation password.');
    } finally {
      setLoading(false);
    }
  };

  return createPortal(<motion.div className={styles.passwordBackdrop} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose}>
    <motion.form className={styles.passwordDialog} initial={{ scale: 0.94, y: 10 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.96, y: 8 }} onClick={(e) => e.stopPropagation()} onSubmit={submit}>
      <div className={styles.passwordDialogIcon}>🔐</div>
      <h2>Operation password</h2>
      <p>Enter the 8-digit password to unlock editing, export, and AI actions for 5 minutes.</p>
      <input type="password" inputMode="numeric" autoFocus maxLength={8} value={password} onChange={(e) => setPassword(e.target.value.replace(/\D/g, ''))} aria-label="Eight-digit operation password" placeholder="••••••••" />
      {error && <div className={styles.passwordError} role="alert">{error}</div>}
      <div className={styles.passwordActions}><button type="button" onClick={onClose}>Cancel</button><button type="submit" disabled={loading}>{loading ? 'Checking…' : 'Unlock'}</button></div>
    </motion.form>
  </motion.div>, document.body);
}

export default function DataScienceCanvas({ allCards, setAllCards, activeSubject, storageKey }) {
  const [captureOpen, setCaptureOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [lightboxPageIndex, setLightboxPageIndex] = useState(null); // number | null
  const [landingNoteId, setLandingNoteId] = useState(null);
  const [newPageToast, setNewPageToast] = useState(false);
  const [unlockOpen, setUnlockOpen] = useState(false);
  const [unlockedUntil, setUnlockedUntil] = useState(0);
  const [pendingAction, setPendingAction] = useState(null);
  const isUnlocked = unlockedUntil > Date.now();

  useEffect(() => {
    if (!unlockedUntil) return undefined;
    const timeout = setTimeout(() => setUnlockedUntil(0), Math.max(0, unlockedUntil - Date.now()));
    return () => clearTimeout(timeout);
  }, [unlockedUntil]);

  const requestUnlock = useCallback((action) => {
    if (unlockedUntil > Date.now()) return action();
    setPendingAction(() => action);
    setUnlockOpen(true);
  }, [unlockedUntil]);

  useEffect(() => {
    const open = () => setUnlockOpen(true);
    window.addEventListener('open-cs-operation-password', open);
    return () => window.removeEventListener('open-cs-operation-password', open);
  }, []);

  const completeUnlock = useCallback(() => {
    setUnlockedUntil(Date.now() + 5 * 60 * 1000);
    setUnlockOpen(false);
    pendingAction?.();
    setPendingAction(null);
  }, [pendingAction]);

  // Distribute cards across pages dynamically (target 8, fallback 6)
  const pages = useMemo(() => distributeToPages(allCards), [allCards]);

  // Sheet refs for PDF export (one ref per page)
  const sheetRefs = useRef([]);
  if (sheetRefs.current.length !== pages.length) {
    sheetRefs.current = pages.map((_, i) => sheetRefs.current[i] || React.createRef());
  }

  // Target ref for fly-to-page: always the last page
  const targetSheetRef = sheetRefs.current[sheetRefs.current.length - 1];

  // Persist to localStorage whenever cards change
  useEffect(() => {
    if (storageKey) {
      localStorage.setItem(storageKey, JSON.stringify(allCards));
    }
  }, [allCards, storageKey]);

  // ── Confirm new AI note ────────────────────────────────────────────────
  const handleConfirmNote = useCallback((newNote) => {
    setCaptureOpen(false);

    // Check if adding this note would overflow the last page
    const currentLastPage = pages[pages.length - 1];
    const maxCapacityForLastPage = currentLastPage.length >= 7 ? 8 : 6;
    const willOverflow = currentLastPage.length >= maxCapacityForLastPage;

    setAllCards(prev => [newNote, ...prev]);
    setLandingNoteId(newNote.id);

    if (willOverflow) {
      setNewPageToast(true);
      setTimeout(() => setNewPageToast(false), 2800);
    }

    setTimeout(() => setLandingNoteId(null), 1200);
  }, [pages, setAllCards]);

  // ── Delete note ────────────────────────────────────────────────────────
  const handleDelete = useCallback((id) => {
    setAllCards(prev => prev.filter(c => c.id !== id));
  }, [setAllCards]);

  // ── Save / edit note ───────────────────────────────────────────────────
  const handleSave = useCallback((id, updatedFields) => {
    setAllCards(prev => prev.map(c => c.id === id ? { ...c, ...updatedFields } : c));
  }, [setAllCards]);

  // ── Lightbox page navigation ──────────────────────────────────────────
  const handleLightboxNavigate = useCallback((delta) => {
    setLightboxPageIndex(prev => {
      if (prev === null) return null;
      const next = prev + delta;
      return Math.max(0, Math.min(next, pages.length - 1));
    });
  }, [pages.length]);

  const totalNotes = allCards.length;

  return (
    <div className={styles.canvas}>
      {/* ── Action bar ─────────────────────────────────────────────────── */}
      <div className={styles.actionBar}>
        <div className={styles.actionBarLeft}>
          <div className={styles.canvasInfoBadge}>
            <div className={styles.canvasInfoDot} />
            {pages.length} Page{pages.length !== 1 ? 's' : ''} · {totalNotes} Note{totalNotes !== 1 ? 's' : ''}
          </div>
          {/* <span className={styles.noteSummary}>
            Max 8 notes/page (auto-adjusts)
          </span> */}
        </div>

        <div className={styles.actionBarRight}>
          {totalNotes > 0 && (
            <button
              className={styles.exportBtn}
              onClick={() => requestUnlock(() => setExportOpen(true))}
            >
              📥 Export PDF
            </button>
          )}
          <button
            className={styles.captureBtn}
            onClick={() => requestUnlock(() => setCaptureOpen(true))}
          >
            ✨ Quick Note (AI)
          </button>
        </div>
      </div>

      {/* ── Sheets area ────────────────────────────────────────────────── */}
      <div className={styles.sheetsArea}>
        {totalNotes === 0 ? (
          <motion.div
            className={styles.emptyCanvas}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.4 }}
          >
            <div className={styles.emptyCanvasIcon}>📋</div>
            <p className={styles.emptyCanvasTitle}>Your canvas is empty</p>
            <p className={styles.emptyCanvasSubtext}>
              Click "Quick Note (AI)" to generate your first study note.
              Notes will be pinned onto A4 pages automatically (up to 8 per sheet).
            </p>
            <div className={styles.emptyCanvasArrow}>
              ↑ Start here
            </div>
          </motion.div>
        ) : (
          pages.map((pageNotes, pageIndex) => (
            <A4Sheet
              key={`page-${pageIndex}`}
              ref={sheetRefs.current[pageIndex]}
              page={pageIndex}
              pageIndex={pageIndex}
              notes={pageNotes}
              onDelete={handleDelete}
              onSave={handleSave}
              isUnlocked={isUnlocked}
              onLockedAction={() => requestUnlock(() => { })}
              isLatest={pageIndex === pages.length - 1}
              landingNoteId={landingNoteId}
              onPaperClick={(idx) => setLightboxPageIndex(idx)}
              subjectTitle={activeSubject?.title}
            />
          ))
        )}
      </div>

      {/* ── Page Lightbox Zoom Modal ────────────────────────────────────── */}
      <AnimatePresence>
        {lightboxPageIndex !== null && (
          <SheetLightbox
            key="sheet-lightbox"
            pages={pages}
            activeIndex={lightboxPageIndex}
            onNavigate={handleLightboxNavigate}
            onClose={() => setLightboxPageIndex(null)}
            onDelete={handleDelete}
            onSave={handleSave}
            isUnlocked={isUnlocked}
            onLockedAction={() => requestUnlock(() => { })}
            subjectTitle={activeSubject?.title}
          />
        )}
      </AnimatePresence>

      {/* ── AI Capture Modal ───────────────────────────────────────────── */}
      <AnimatePresence>
        {captureOpen && (
          <AICaptureModal
            key="capture-modal"
            onClose={() => setCaptureOpen(false)}
            onConfirm={handleConfirmNote}
            targetSheetRef={targetSheetRef}
            subject={activeSubject?.title || 'Data Science'}
          />
        )}
      </AnimatePresence>

      {/* ── PDF Export Modal ───────────────────────────────────────────── */}
      <AnimatePresence>
        {exportOpen && (
          <PDFExportModal
            key="export-modal"
            onClose={() => setExportOpen(false)}
            sheetRefs={sheetRefs.current}
            totalPages={pages.length}
            filename={`${(activeSubject?.id || 'study-notes').replace(/[^a-z0-9]+/gi, '-')}-notes.pdf`}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>{unlockOpen && <OperationPasswordModal onClose={() => { setUnlockOpen(false); setPendingAction(null); }} onUnlocked={completeUnlock} />}</AnimatePresence>

      {/* ── New page toast ─────────────────────────────────────────────── */}
      <AnimatePresence>
        {newPageToast && (
          <motion.div
            className={styles.newPageToast}
            initial={{ opacity: 0, y: 20, scale: 0.92 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.95 }}
            transition={{ type: 'spring', stiffness: 380, damping: 28 }}
          >
            <div className={styles.newPageDot} />
            Page {pages.length} created automatically
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
